import { Box, Button } from "@mui/material";
import { CommonImageComp, adaptiveTextGroups } from "../../components/ui";
import { PreviewImageButton } from "../../components/ui/PreviewImageWrapper";
import type { SpecifyControlBinding } from "../specify/types";
import { rowTextStyle } from "../../components/ui/GridCommonLableWithImageComp/style";
import { useThemeContext } from "../../globalContext/global-context";

type GridRowContentProps = {
  row: {
    id?: string;
    label: string;
    image?: string;
    displayLabel: boolean;
    customPrompt?: string;
  };
  compact: boolean;
  onImageSettled?: () => void;
  specify?: SpecifyControlBinding;
};

export const GridRowContent = ({
  row,
  compact,
  onImageSettled,
  specify,
}: GridRowContentProps) => {
  const { globalObject } = useThemeContext();
  const theme = globalObject.themeData.normalizedTheme;

  const field = specify?.fields.find(
    (item) => item.target.kind === "row-label" && item.target.rowId === row.id,
  );
  const label = field?.value || row.label;
  const content = (
    <CommonImageComp
      currentVal={{
        image: row.image,
        text: label,
        display_text: field !== undefined || row.displayLabel,
        alt_text: label,
      }}
      layout={compact ? "inline" : "stacked"}
      keepLabelWhileLoading
      adaptiveText={{
        group: adaptiveTextGroups.gridRowHeaders,
        maxLines: 3,
      }}
      sx={
        compact
          ? { color: theme.tokens.color.text.primary, width: "auto" }
          : {
              ...rowTextStyle(theme, Boolean(row.image)),
              maxWidth: "100%",
            }
      }
      imageStyle={{
        maxHeight: compact ? "50px" : "60px",
        maxWidth: compact ? 40 : 80,
      }}
      onSettled={onImageSettled}
      disablePreviewWrapper={field !== undefined}
    />
  );
  if (!field || row.id === undefined) return content;
  const editButton = (
    <Button
      data-testid={`specify-row-${row.id}`}
      aria-label={`${label}: ${field.prompt}`}
      disabled={field.disabled}
      onClick={() => specify!.open({ kind: "row-label", rowId: row.id! })}
      sx={{ textTransform: "none", textAlign: "start", minWidth: 0 }}
    >
      {content}
    </Button>
  );
  if (!row.image) return editButton;
  return (
    <Box
      sx={{
        position: "relative",
        display: "inline-flex",
        "& .preview-icon": { opacity: 0 },
        "&:hover .preview-icon, &:focus-within .preview-icon": { opacity: 1 },
        "@media (hover: none)": { "& .preview-icon": { opacity: 1 } },
      }}
    >
      {editButton}
      <PreviewImageButton imageToPreview={row.image} altText={label} />
    </Box>
  );
};
